import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { ArrowIcon } from '../../assets';
import {
  BORDER_RADIUS_MAIN,
  CUSTOMER_TYPES,
  SHIPPING_ITEMS_VIEW_TYPES,
  ORDER_DATE_FORMAT,
  SHIPPING_TYPES,
} from '../../constants/globals';
import { COLORS } from '../../constants';
import { FONT_CART_VARIANT_SUB, FONT_FAMILY, FONT_SIZES } from '../../constants/fonts';
import { withRouter } from '../../hocs';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import { makeStringAddress, weightToUser } from '../../helpers';
import moment from 'moment';

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',

    '@media print': {
      display: 'block',
    },
  },
  singleItem: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    marginBottom: 20,
    paddingTop: 10,
    paddingBottom: 10,
    paddingLeft: 5,
    paddingRight: 5,
    marginLeft: -5,
    marginRight: -5,
    boxSizing: 'border-box',
    borderRadius: BORDER_RADIUS_MAIN,
    cursor: 'pointer',
    backgroundColor: COLORS.white,
    transition: 'background-color 0.3s ease-in-out',
    '&:hover': {
      backgroundColor: COLORS.backgroundLight,
    },
  },
  simpleViewCustomer: {
    paddingBottom: 15,
    paddingTop: 15,
    marginBottom: 10,
  },
  customerName: {
    paddingBottom: 7,
    paddingTop: 7,
    marginBottom: 5,
  },
  locationName: {
    paddingBottom: 7,
    paddingTop: 7,
    marginBottom: 5,
  },
  singleItemTree: {
    marginBottom: 0,
    paddingTop: 3,
    paddingBottom: 3,
  },
  costumerItem: {
    paddingLeft: 25,
  },
  lineItem: {
    paddingLeft: 45,
    cursor: 'auto',
  },
  lastLineItem: {
    marginBottom: 10,
  },
  itemContentWrapper: {
    flex: 1,
  },
  itemTitle: {
    fontFamily: FONT_FAMILY,
    fontSize: FONT_SIZES.label,
    fontWeight: '700',
    marginBottom: 0,
    color: COLORS.blackTintedBlue,
  },
  itemLiteTitle: {
    fontFamily: FONT_FAMILY,
    fontSize: FONT_SIZES.text,
    fontWeight: '700',
    marginBottom: 0,
    color: COLORS.blackTintedBlue,
  },
  itemSubTitle: {
    fontFamily: FONT_FAMILY,
    fontSize: FONT_SIZES.label,
    fontWeight: '500',
    color: COLORS.grey,
    marginBottom: 5,
  },
  itemContent: {
    fontFamily: FONT_FAMILY,
    fontSize: FONT_SIZES.label,
    fontWeight: '400',
    color: COLORS.blackTintedBlue,
  },
  arrowForward: {
    paddingLeft: 20,
  },
  lineItemWrapper: {
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    borderRadius: BORDER_RADIUS_MAIN,
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 5,
    paddingRight: 5,
    marginLeft: -5,
    marginRight: -5,
  },
  lineItemImage: {
    width: 56,
    height: 56,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    overflow: 'hidden',
    borderRadius: BORDER_RADIUS_MAIN,
    marginRight: 10,
  },
  itemImage: {
    position: 'absolute',
    maxHeight: 60,
  },
  lineItemContent: {
    flex: 1,
  },
  lineItemName: {
    fontFamily: FONT_FAMILY,
    fontSize: FONT_SIZES.label,
    fontWeight: '400',
    marginBottom: 0,
    color: COLORS.blackTintedBlue,
  },
  lineItemPrice: {
    ...FONT_CART_VARIANT_SUB,
    color: COLORS.grey,
  },
  lineItemOrdered: {
    fontFamily: FONT_FAMILY,
    fontSize: FONT_SIZES.label,
    fontWeight: '700',
    paddingLeft: 20,
    color: COLORS.blackTintedBlue,
  },
  pageBreak: {
    '@media print': {
      breakAfter: 'always',
      breakBefore: 'always',
      pageBreakAfter: 'always',
      pageBreakBefore: 'always',
    },
  },
}));

const propTypes = {
  unlisted: PropTypes.bool,
};

const ShippingItemsViewComponent = (props) => {
  const { items, history, day, viewType } = props;
  const intl = useIntl();
  const isLiteView = viewType === SHIPPING_ITEMS_VIEW_TYPES.LITE;

  const classes = useStyles();

  const drawSingleItem = (item) => {
    if (isLiteView) {
      return drawLiteView(item);
    }

    return drawTreeView(item);
  };

  const drawTreeView = (item) => {
    if (item?.shippingType === SHIPPING_TYPES.SHIPPING_DELIVERY) {
      return (
        <React.Fragment key={`loc_${item?.id}`}>
          {/*
          // customer level
          <div className={classNames([classes.singleItem, classes.customerName])} onClick={() => onCustomerClick(item)}>
            {renderCustomer(item)}
          </div>
          */}
          {drawOrders(item)}
          <div className={classes.pageBreak} />
        </React.Fragment>
      );
    }

    return (
      <React.Fragment key={`loc_${item?.id}`}>
        <div className={classNames([classes.singleItem, classes.locationName])} onClick={() => onCustomerClick(item)}>
          {renderLocation(item)}
        </div>
        {drawLocationOrders(item)}
        <div className={classes.pageBreak} />
      </React.Fragment>
    );
  };

  const drawLiteView = (item) => {
    if (item?.shippingType === SHIPPING_TYPES.SHIPPING_DELIVERY) {
      return (
        <div
          key={`item_${item?.id}`}
          className={classNames([classes.singleItem, classes.simpleViewCustomer])}
          onClick={() => onCustomerClick(item)}>
          {renderCustomer(item)}
          <div className={classes.arrowForward}>
            <ArrowIcon />
          </div>
        </div>
      );
    }

    return (
      <div
        key={`item_${item?.id}`}
        className={classNames([classes.singleItem, classes.simpleViewLocation])}
        onClick={() => onCustomerClick(item)}>
        {renderLocation(item)}
        <div className={classes.arrowForward}>
          <ArrowIcon />
        </div>
      </div>
    );
  };

  const renderLocation = (location) => {
    return (
      <div className={classes.itemContentWrapper}>
        <div className={isLiteView ? classes.itemLiteTitle : classes.itemTitle}>{location.name}</div>
        {!isLiteView && (
          <div className={classes.itemContent}>
            {intl.formatMessage(
              { id: 'deliveries.locations.pickupList' },
              { date: moment.parseZone(day).format(ORDER_DATE_FORMAT) }
            )}
          </div>
        )}
      </div>
    );
  };

  const drawLocationOrders = (location) => {
    return location.orders.map((order) => (
      <React.Fragment key={`ord_${order.id}`}>
        <div
          className={classNames([classes.singleItem, classes.singleItemTree, classes.costumerItem])}
          onClick={() => onOrderClick(location, order)}>
          <div className={classes.itemContentWrapper}>
            <div className={classes.itemTitle}>{drawLocationCustomerName(order)}</div>
          </div>
        </div>
        {drawLineItems(location, order)}
      </React.Fragment>
    ));
  };

  const drawOrders = (customer) => {
    return customer.orders.map((order) => (
      <React.Fragment key={`ord_${order.id}`}>
        <div
          className={classNames([classes.singleItem, classes.customerName])}
          onClick={() => onCustomerClick(customer)}>
          <div className={classes.itemContentWrapper}>
            <div className={classes.itemTitle}>{makeStringAddress(order.customer.shippingAddress)}</div>
          </div>
        </div>
        <div
          className={classNames([classes.singleItem, classes.singleItemTree, classes.costumerItem])}
          onClick={() => onOrderClick(customer, order)}>
          <div className={classes.itemContentWrapper}>
            <div className={classes.itemTitle}>{`${order.name}`}</div>
          </div>
        </div>
        {drawLineItems(customer, order)}
      </React.Fragment>
    ));
  };

  const drawLineItems = (customer, order) => {
    return order.lineItems.map((lineItem, index) => (
      <div
        key={`${order.id}_${lineItem.id}`}
        className={classNames([
          classes.singleItem,
          classes.singleItemTree,
          classes.lineItem,
          order?.lineItems?.length - 1 === index && classes.lastLineItem,
        ])}>
        {drawLineItem(lineItem)}
      </div>
    ));
  };

  const drawCustomerName = (customer) => {
    if (customer?.type === CUSTOMER_TYPES.WHOLESALE) return customer?.companyName || '';
    if (customer?.type === CUSTOMER_TYPES.ANONYMOUS) return intl.formatMessage({ id: 'deliveries.sales.anonymous' });

    return `${customer?.firstName ? customer.firstName + ' ' : ''}${customer?.lastName ? customer.lastName : ''}${
      customer?.email ? ' / ' + customer.email : ''
    }${customer?.phone ? ' / ' + customer.phone : ''}`;
  };

  const drawLocationCustomerName = (order) => {
    if (order?.customer?.type === CUSTOMER_TYPES.WHOLESALE) return order.customer.companyName;
    if (order?.customer?.type === CUSTOMER_TYPES.ANONYMOUS)
      return intl.formatMessage({ id: 'pickups.sales.anonymous' });

    return `${order.customer.firstName ? order.customer.firstName + ' ' : ''}${
      order.customer.lastName ? order.customer.lastName : ''
    }${order?.customer?.email ? ' / ' + order.customer.email : ''}${
      order?.customer?.phone ? ' / ' + order.customer.phone : ''
    }`;
  };

  const drawLineItem = (lineItem) => {
    return (
      <div className={classes.lineItemWrapper}>
        <div className={classes.lineItemContent}>
          <p className={classes.lineItemName}>{getProductName(lineItem)}</p>
        </div>
        <p className={classes.lineItemOrdered}>{weightToUser(lineItem.orderedQuantity)}</p>
      </div>
    );
  };

  const getProductName = (lineItem) => {
    if (lineItem.name.variant === lineItem.unit)
      return `${lineItem.name.product}${!!lineItem?.unit ? ' / ' + lineItem.unit : ''}`;

    if (typeof lineItem.name === 'string') return lineItem.name;

    return `${lineItem.name.product}${!!lineItem?.name?.variant ? ' / ' + lineItem.name.variant : ''}${
      lineItem?.unit && lineItem?.unit !== lineItem.name.variant ? ' / ' + lineItem.unit : ''
    }`;
  };

  const renderCustomer = (customer) => {
    return (
      <div className={classes.itemContentWrapper}>
        <div className={isLiteView ? classes.itemLiteTitle : classes.itemTitle}>{drawCustomerName(customer)}</div>
      </div>
    );
  };

  const onCustomerClick = (customer) => history.push(`/app/deliveries/day/${day}/location/${customer.id}/orders`);
  const onOrderClick = (customer, order) => history.push(`/app/deliveries/orders/${order.id}`);

  return <div className={classes.root}>{!!items?.length && items.map(drawSingleItem)}</div>;
};

ShippingItemsViewComponent.propTypes = propTypes;

export default withRouter(ShippingItemsViewComponent);
